import { FC, useMemo } from 'react';
import classNames from 'classnames/bind';
import { LoaderType, NextgenImage } from '../NextgenImage/NextgenImage';
import RawButton from '../Button/RawButton';
import GenericLink from '../GenericLink/GenericLink';
import { Breakpoints, useBreakpoint } from '../Breakpoint/BreakpointProvider';
import { CtaBtnAlignment } from './types/enums';
import { ImageWithOptionalCTAProptypes } from './types/propsType';
import styles from './ImageWithOptionalCTA.module.scss';

const cx = classNames.bind(styles);

const ImageWithOptionalCTA: FC<ImageWithOptionalCTAProptypes> = ({
  containerBorderRadius,
  bgColor = 'transparent',
  ctaConfig,
  button,
  imgPerViewport,
  className,
  layout: _layout,
  fill: _,
  loaderType = LoaderType.CONTENTSTACK,
  ...restProps
}: ImageWithOptionalCTAProptypes) => {
  const viewport = useBreakpoint() as Breakpoints;
  const { alignment, borderDistance, ...rawButtonProps } = button || {};
  const {
    src,
    alt,
    borderRadius,
    width = 100,
  } = useMemo(() => imgPerViewport[viewport || Breakpoints.MD], [viewport, imgPerViewport]);
  const btnLocationStyle = () => {
    let style = {};
    if (alignment === CtaBtnAlignment.BOTTOM_CENTER) {
      style = { bottom: borderDistance ?? styles.borderButtonDistance };
    } else if (alignment === CtaBtnAlignment.TOP_CENTER) {
      style = { top: borderDistance ?? styles.borderButtonDistance };
    }
    return style;
  };
  const CClassname = cx('img-container', { 'center-alignment': width }, className);
  const CBClassname = cx('img-body', { [alignment ?? CtaBtnAlignment.BOTTOM_CENTER]: Boolean(button?.title) });
  return (
    <section
      className={CClassname}
      style={{ backgroundColor: bgColor, borderRadius: containerBorderRadius }}
      data-testid="image-WithOptionalCTA-testid"
    >
      <div className={CBClassname}>
        {ctaConfig?.href && !button?.title ? (
          <GenericLink
            href={ctaConfig?.href ?? ''}
            target={ctaConfig?.target}
            className={styles['cta-anchor']}
            data-testid="cta-image-testid"
          >
            <NextgenImage
              {...restProps}
              src={src}
              alt={alt}
              className={styles['img-main']}
              sizes="100vw"
              loading="lazy"
              width={0}
              height={0}
              fill={false}
              decoding="async"
              style={{
                height: 'auto',
                color: 'transparent',
                width: `${width ?? 100}%`,
                ...(borderRadius ? { borderRadius: borderRadius } : {}),
              }}
              loaderType={loaderType}
            />
          </GenericLink>
        ) : (
          <NextgenImage
            {...restProps}
            src={src}
            alt={alt}
            className={styles['img-main']}
            sizes="100vw"
            loading="lazy"
            width={0}
            height={0}
            fill={false}
            decoding="async"
            style={{
              height: 'auto',
              color: 'transparent',
              width: `${width ?? 100}%`,
              ...(borderRadius ? { borderRadius: borderRadius } : {}),
            }}
            loaderType={loaderType}
          />
        )}
        {button?.title && ctaConfig?.href && (
          <GenericLink
            href={ctaConfig.href}
            target={ctaConfig.target}
            className={styles['cta-btn']}
            data-testid="cta-btn-testid"
            style={btnLocationStyle()}
          >
            <RawButton {...rawButtonProps} />
          </GenericLink>
        )}
      </div>
    </section>
  );
};
ImageWithOptionalCTA.displayName = 'ImageWithOptionalCTA';
export { CtaBtnAlignment };
export type { ImageWithOptionalCTAProptypes };
export default ImageWithOptionalCTA;
